import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { collection, onSnapshot } from 'firebase/firestore';
import { db } from '../firebase';
import LoadingSpinner from '../components/ui/LoadingSpinner';
import FactionCardRoster from '../components/faction/FactionCardRoster';
import { calculateFactionTotalScore } from '../utils/factionScoring';
import { getFactionStoredWrestlers } from '../utils/factionWrestlers';
import { calculateGmMatchPointsByFaction, normalizeGmMatch } from '../utils/gmMatches';
import { autoCloseExpiredGmMatches } from '../utils/gmMatchSync';

function Factions() {
  const [rawFactions, setRawFactions] = useState([]);
  const [wrestlerData, setWrestlerData] = useState({});
  const [usersMap, setUsersMap] = useState({});
  const [wrestlerScores, setWrestlerScores] = useState({});
  const [rostersData, setRostersData] = useState({});
  const [gmScoresByRoster, setGmScoresByRoster] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showLegend, setShowLegend] = useState(false);
  const [activeTab, setActiveTab] = useState('rosters'); // New state for active tab


  // UI states
  const [searchTerm, setSearchTerm] = useState('');
  const [memberFilter, setMemberFilter] = useState('');
  const [rosterFilter, setRosterFilter] = useState('');
  const [sortBy, setSortBy] = useState('name');
  const [sortOrder, setSortOrder] = useState('asc');
  const [userSearch, setUserSearch] = useState('');
  const [rosterSearch, setRosterSearch] = useState('');

  const navigate = useNavigate();

  // Timeout per caricamenti troppo lunghi
  useEffect(() => {
    const timeout = setTimeout(() => {
      if (loading) {
        setError("Il caricamento sta impiegando più tempo del previsto");
        setLoading(false);
      }
    }, 10000);

    return () => clearTimeout(timeout);
  }, [loading]);

  // 1) Carica tutti i wrestler
  useEffect(() => {
    const unsub = onSnapshot(
      collection(db, 'wrestlers'),
      snap => {
        const map = {};
        const scores = {};
        snap.docs.forEach(d => {
          const w = d.data();
          map[d.id] = {
            name: w.name || w.nome || '—',
            imageUrl: w.imageUrl || 'https://via.placeholder.com/40'
          };
          scores[d.id] = w.score || 0;
        });
        setWrestlerData(map);
        setWrestlerScores(scores);
      },
      err => {
        console.error("Errore nel caricamento wrestler:", err);
        setError("Errore nel caricamento dei dati wrestler");
        setLoading(false);
      }
    );
    return () => unsub();
  }, []);

  // 2) Carica utenti per ownerName
  useEffect(() => {
    const unsub = onSnapshot(
      collection(db, 'users'),
      snap => {
        const m = {};
        snap.docs.forEach(d => {
          const userData = d.data();
          m[d.id] = {
            username: userData.username || '—',
            profileImageUrl: userData.profileImageUrl || null
          };
        });
        setUsersMap(m);
      },
      err => {
        console.error("Errore nel caricamento utenti:", err);
        setError("Errore nel caricamento dei dati utente");
        setLoading(false);
      }
    );
    return () => unsub();
  }, []);

  // 3) Carica fazioni
  useEffect(() => {
    const unsub = onSnapshot(
      collection(db, 'factions'),
      snap => {
        const factionsData = snap.docs.map(d => ({ id: d.id, ...d.data() }));
        console.log("Dati fazioni caricati:", factionsData); // Verifica che createdAt sia presente
        setRawFactions(factionsData);
        setLoading(false);
      },
      err => {
        console.error("Errore nel caricamento fazioni:", err);
        setError("Errore nel caricamento delle fazioni");
        setLoading(false);
      }
    );
    return () => unsub();
  }, []);

  // 4) Costruisci le fazioni derivate
  const factions = useMemo(() => {
    try {
      return rawFactions.map(data => {
        const storedMembers = getFactionStoredWrestlers(data);
        const rosterMeta = data.rosterId
          ? rostersData[data.rosterId]
          : Object.values(rostersData).find(roster => roster.name === data.roster);
        const isGmRoster = Boolean(rosterMeta?.isGM);

        const members = storedMembers.map(m => ({
          id: m.id,
          name: wrestlerData[m.id]?.name,
          imageUrl: wrestlerData[m.id]?.imageUrl,
        }));

        const total = isGmRoster
          ? (gmScoresByRoster[rosterMeta.id]?.[data.id] || 0)
          : calculateFactionTotalScore({
              faction: data,
              roster: rosterMeta,
              members: storedMembers,
              wrestlerScoresById: wrestlerScores,
            });
        return {
          id: data.id,
          name: data.name || 'Senza nome',
          ownerUid: data.ownerUid,
          ownerName: usersMap[data.ownerUid]?.username || 'Sconosciuto',
          imageUrl: data.imageUrl || '',
          roster: rosterMeta?.name || data.roster || 'Indy',
          rosterId: rosterMeta?.id || data.rosterId || null,
          gameType: rosterMeta?.gameType || data.gameType,
          isGM: isGmRoster,
          creditsLeft: data.creditsLeft || 0,
          totalScore: total,
          members,
          createdAt: data.createdAt
        };
      });
    } catch (err) {
      console.error("Errore nel calcolo delle fazioni:", err);
      setError("Errore nel processare i dati delle fazioni");
      return [];
    }
  }, [gmScoresByRoster, rawFactions, rostersData, wrestlerData, wrestlerScores, usersMap]);

  //5) Carica i roster dalla collezione separata
  useEffect(() => {
    const unsub = onSnapshot(
      collection(db, 'rosters'),
      (snap) => {
        const data = {};
        snap.docs.forEach(doc => {
          data[doc.id] = { id: doc.id, ...doc.data() }; // Aggiungi l'ID qui
        });
        setRostersData(data);
      },
      (err) => {
        console.error("Errore nel caricamento roster:", err);
      }
    );
    return () => unsub();
  }, []);

  useEffect(() => {
    const gmRosters = Object.values(rostersData).filter(roster => roster.isGM);
    const activeRosterIds = new Set(gmRosters.map(roster => roster.id));

    setGmScoresByRoster(prev =>
      Object.keys(prev).reduce((next, rosterId) => {
        if (activeRosterIds.has(rosterId)) {
          next[rosterId] = prev[rosterId];
        }
        return next;
      }, {})
    );

    const unsubs = gmRosters.map(roster =>
      onSnapshot(
        collection(db, 'rosters', roster.id, 'matches'),
        async snap => {
          const normalizedMatches = snap.docs.map(docSnap => normalizeGmMatch(docSnap));
          const { matches } = await autoCloseExpiredGmMatches({
            db,
            rosterId: roster.id,
            matches: normalizedMatches,
            roster,
            persist: false
          });
          setGmScoresByRoster(prev => ({
            ...prev,
            [roster.id]: calculateGmMatchPointsByFaction(matches)
          }));
        },
        err => {
          console.error("Errore nel caricamento match GM:", err);
        }
      )
    );

    return () => unsubs.forEach(unsub => unsub());
  }, [rostersData]);

  const rosterGroups = useMemo(() => {
    // Create a normalized roster metadata map (case-insensitive)
    const rosterMetadataMap = new Map();
    if (rostersData) {
      const rostersArray = Array.isArray(rostersData)
        ? rostersData
        : Object.values(rostersData);
      rostersArray.forEach(roster => {
        if (roster.name) {
          const normalizedKey = String(roster.name || "").trim().toLowerCase();
          rosterMetadataMap.set(normalizedKey, roster);
        }
      });
    }
    const rostersArray = Object.entries(rostersData).map(([id, data]) => ({
      id,
      ...data
    }));

    // Group factions by roster
    const groups = {};
    factions.forEach(faction => {
      const rawRosterName = faction.roster || 'Indy';
      const normalizedRoster = String(rawRosterName || "").trim().toLowerCase();
      if (!groups[normalizedRoster]) {
        const metadata = rosterMetadataMap.get(normalizedRoster) || {};
        groups[normalizedRoster] = {
          id: metadata.id || normalizedRoster, // Usa l'ID se disponibile
          displayName: metadata.name || rawRosterName,
          normalizedKey: normalizedRoster,
          factions: [],
          totalScore: 0,
          gameType: metadata.gameType || 'Unknown',
          createdAt: metadata.createdAt || null,
        };
      }
      groups[normalizedRoster].factions.push(faction);
      groups[normalizedRoster].totalScore += faction.totalScore;
    });

    // Ordina per data di creazione (createdAt, decrescente)
    return Object.values(groups).sort((a, b) => {
      if (!a.createdAt && !b.createdAt) return 0;
      if (!a.createdAt) return 1;
      if (!b.createdAt) return -1;
      const aDate = a.createdAt.seconds ? a.createdAt.seconds : (a.createdAt.toDate ? a.createdAt.toDate().getTime() / 1000 : new Date(a.createdAt).getTime() / 1000);
      const bDate = b.createdAt.seconds ? b.createdAt.seconds : (b.createdAt.toDate ? b.createdAt.toDate().getTime() / 1000 : new Date(b.createdAt).getTime() / 1000);
      return bDate - aDate;
    });
  }, [factions, rostersData]);


  const userList = useMemo(() => {
    let list = Object.entries(usersMap).map(([uid, userData]) => {
      const userFaction = factions.find(f => f.ownerUid === uid);
      return {
        uid,
        username: userData.username,
        profileImageUrl: userData.profileImageUrl,
        faction: userFaction ? {
          id: userFaction.id,
          name: userFaction.name,
          score: userFaction.totalScore,
        } : null
      };
    });
    // Filtro per nome utente
    if (userSearch) {
      const s = userSearch.toLowerCase();
      list = list.filter(u => u.username.toLowerCase().includes(s));
    }
    // Ordina sempre per nome
    return list.sort((a, b) => a.username.localeCompare(b.username, 'it', { sensitivity: 'base' }));
  }, [usersMap, factions, userSearch]);


  // Calcola i wrestler più scelti
  const topWrestlers = useMemo(() => {
    const wrestlerCount = {};

    // Conta quante volte ogni wrestler appare nelle fazioni
    factions.forEach(faction => {
      faction.members.forEach(member => {
        if (member.id) {
          wrestlerCount[member.id] = (wrestlerCount[member.id] || 0) + 1;
        }
      });
    });

    // Converti in array e ordina
    return Object.entries(wrestlerCount)
      .map(([id, count]) => ({
        id,
        name: wrestlerData[id]?.name || 'Wrestler sconosciuto',
        count
      }))
      .sort((a, b) => b.count - a.count || a.name.localeCompare(b.name))
      .slice(0, 10); // Prendi solo i top 10
  }, [factions, wrestlerData]);

  // Valori unici dei roster per il dropdown
  const rosterOptions = useMemo(() => {
    const setOptions = new Set(factions.map(f => f.roster));
    return [''].concat(Array.from(setOptions));
  }, [factions]);

  // 5) Filtri e ordinamento
  const filtered = useMemo(() => {
    let list = factions;

    if (searchTerm) {
      const s = searchTerm.toLowerCase();
      list = list.filter(f => f.name.toLowerCase().includes(s));
    }
    if (memberFilter) {
      const mf = memberFilter.toLowerCase();
      list = list.filter(f =>
        f.members.some(m => m.name && m.name.toLowerCase().includes(mf))
      );
    }
    if (rosterFilter) {
      list = list.filter(f => (f.roster || '').toLowerCase() === rosterFilter.toLowerCase());
    }

    list = list.slice().sort((a, b) => {
      let cmp;
      if (sortBy === 'name') {
        cmp = a.name.localeCompare(b.name, 'it', { sensitivity: 'base' });
      } else {
        cmp = a.totalScore - b.totalScore;
      }
      return sortOrder === 'asc' ? cmp : -cmp;
    });

    return list;
  }, [factions, searchTerm, memberFilter, rosterFilter, sortBy, sortOrder]);

  if (loading) {
    return <LoadingSpinner fullPage={true} />;
  }

  const formatDate = (timestamp) => {
    if (!timestamp) return 'Data sconosciuta';

    try {
      let date;

      // Se è un oggetto Timestamp di Firestore (con seconds e nanoseconds)
      if (timestamp.seconds !== undefined && timestamp.nanoseconds !== undefined) {
        date = new Date(timestamp.seconds * 1000);
      }
      // Se ha il metodo toDate (Timestamp di Firestore)
      else if (typeof timestamp.toDate === 'function') {
        date = timestamp.toDate();
      }
      // Se è già un oggetto Date
      else if (timestamp instanceof Date) {
        date = timestamp;
      }
      // Se è una stringa o numero
      else {
        date = new Date(timestamp);
      }

      // Verifica che la data sia valida
      if (isNaN(date.getTime())) {
        return 'Data non valida';
      }

      return date.toLocaleDateString('it-IT', {
        day: 'numeric',
        month: 'long',
        year: 'numeric'
      });
    } catch (error) {
      console.error('Errore nella formattazione della data:', error, timestamp);
      return 'Data non valida';
    }
  };

  // Oppure se vuoi includere anche l'ora:
  /*const formatDateTime = (timestamp) => {
    if (!timestamp) return 'Data sconosciuta';

    const options = {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    };

    if (timestamp.toDate) {
      return timestamp.toDate().toLocaleDateString('it-IT', options);
    }

    if (timestamp instanceof Date) {
      return timestamp.toLocaleDateString('it-IT', options);
    }

    return new Date(timestamp).toLocaleDateString('it-IT', options);
  };*/

  if (error) {
    return (
      <div className="factions-page py-4">
        <div className="container text-center py-5">
          <div className="alert factions-error-box">
            <i className="bi bi-exclamation-triangle-fill me-2"></i>
            {error}
          </div>
          <button
            className="btn stats-action-btn mt-3"
            onClick={() => window.location.reload()}
          >
            <i className="bi bi-arrow-clockwise me-2"></i>
            Ricarica la pagina
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="factions-page py-2">
      <div className="container">
        {/* Tab Navigation come parte dell'intestazione */}
        <ul className="nav nav-tabs justify-content-center factions-tabs" style={{ borderBottom: 'none' }}>
          <li className="nav-item">
            <button
              className={`nav-link ${activeTab === 'factions' ? 'active' : ''}`}
              onClick={() => setActiveTab('factions')}
            >
              <i className="bi bi-people-fill me-1"></i> Fazioni
            </button>
          </li>
          <li className="nav-item">
            <button
              className={`nav-link ${activeTab === 'rosters' ? 'active' : ''}`}
              onClick={() => setActiveTab('rosters')}
            >
              <i className="bi bi-collection-fill me-1"></i> Roster
            </button>
          </li>
          <li className="nav-item">
            <button
              className={`nav-link ${activeTab === 'users' ? 'active' : ''}`}
              onClick={() => setActiveTab('users')}
            >
              <i className="bi bi-person-lines-fill me-1"></i> Giocatori
            </button>
          </li>
        </ul>

        {/* Factions Tab */}
        {activeTab === 'factions' && (
          <div className="row">
            <div className="col-lg-9">
              {/* Barra ricerca + filtri */}
              <div className="row g-2 mb-4 sticky-top py-2 factions-filter-bar">
                <div className="col-12 col-md-3">
                  <input
                    type="text"
                    className="form-control stats-control"
                    placeholder="🔍 Cerca fazione..."
                    value={searchTerm}
                    onChange={e => setSearchTerm(e.target.value)}
                  />
                </div>
                <div className="col-12 col-md-3">
                  <input
                    type="text"
                    className="form-control stats-control"
                    placeholder="👤 Cerca wrestler..."
                    value={memberFilter}
                    onChange={e => setMemberFilter(e.target.value)}
                  />
                </div>
                <div className="col-12 col-md-3">
                  <select
                    className="form-select stats-control"
                    value={rosterFilter}
                    onChange={e => setRosterFilter(e.target.value)}
                  >
                    <option value="">Tutti i roster</option>
                    {rosterOptions
                      .filter(opt => opt)
                      .sort((a, b) => a.localeCompare(b)) // Ordine alfabetico
                      .map(opt => (
                        <option key={opt} value={opt}>{opt}</option>
                      ))}
                  </select>
                </div>
                <div className="col-6 col-md-2">
                  <select
                    className="form-select stats-control"
                    value={sortBy}
                    onChange={e => setSortBy(e.target.value)}
                  >
                    <option value="name">Ordina per Nome</option>
                    <option value="score">Ordina per Punti</option>
                  </select>
                </div>
                <div className="col-6 col-md-2">
                  <select
                    className="form-select stats-control"
                    value={sortOrder}
                    onChange={e => setSortOrder(e.target.value)}
                  >
                    <option value="asc">Crescente</option>
                    <option value="desc">Decrescente</option>
                  </select>
                </div>
                <button
                  className="btn results-view-btn d-lg-none"
                  onClick={() => setShowLegend(true)}
                >
                  <i className="bi bi-bar-chart me-2"></i>
                  Wrestler più scelti
                </button>
              </div>

              {filtered.length === 0 ? (
                <div className="text-center p-5 empty-state factions-empty-state">
                  <p className="text-muted-brand fs-5 mb-0">Nessuna fazione trovata</p>
                </div>
              ) : (
                <div className="row g-4">
                  {filtered.map(f => (
                    <div className="col-12 col-md-6 col-lg-4" key={f.id}>
                      <FactionCardRoster
                        faction={f}
                        ownerName={f.ownerName}
                        members={f.members}
                        onClick={() => navigate(`/factions/${f.id}`)}
                      />
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Legenda wrestler più scelti (Desktop) */}
            <div className="col-lg-3 d-none d-lg-block">
              <div className="card mb-4 factions-side-card">
                <div className="card-header factions-side-card-header">
                  <h5 className="mb-0">Wrestler più scelti</h5>
                </div>
                <div className="card-body p-0">
                  <ul className="list-group list-group-flush">
                    {topWrestlers.length > 0 ? (
                      topWrestlers.map((wrestler, index) => (
                        <li
                          key={wrestler.id}
                          className="list-group-item d-flex justify-content-between align-items-center factions-list-item"
                        >
                          <div className="d-flex align-items-center">
                            <span className="text-brand-secondary fw-bold me-2">{index + 1}.</span>
                            <span className="text-light">{wrestler.name}</span>
                          </div>
                          <span className="badge bg-brand-primary rounded-pill">
                            {wrestler.count}
                          </span>
                        </li>
                      ))
                    ) : (
                      <li className="list-group-item text-center text-muted-brand py-4 factions-list-item">
                        Nessun wrestler trovato
                      </li>
                    )}
                  </ul>
                </div>
                <div className="card-footer small text-muted-brand factions-side-card-footer">
                  Numero di fazioni in cui compare
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Rosters Tab */}
        {activeTab === 'rosters' && (
          <>
            <div className="mb-3">
              <input
                type="text"
                className="form-control stats-control"
                placeholder="🔍 Cerca roster..."
                value={rosterSearch}
                onChange={e => setRosterSearch(e.target.value)}
              />
            </div>
            <div className="row g-3">
              {rosterGroups
                .filter(roster => {
                  const dn = roster.displayName || '';
                  return String(dn).toLowerCase().includes(rosterSearch.toLowerCase());
                })
                .map(roster => {
                  let borderColor, icon, iconColor;
                  if (roster.gameType === 'Competitivo') {
                    borderColor = '#28a745';
                    icon = 'bi-trophy-fill';
                    iconColor = '#28a745';
                  } else if (roster.gameType === 'Indipendente') {
                    borderColor = '#007bff';
                    icon = 'bi-list-ul';
                    iconColor = '#007bff';
                  } else {
                    borderColor = '#6c757d';
                    icon = 'bi-collection-fill';
                    iconColor = '#6c757d';
                  }

                  return (
                    <div className="col-12 col-md-6 col-lg-4" key={roster.normalizedKey}>
                      <div
                        className="card roster-card h-100 shadow border-3 clickable factions-roster-card"
                        style={{
                          cursor: 'pointer',
                          height: '100%',
                          borderColor,
                          borderWidth: '2px',
                          borderStyle: 'solid',
                          boxShadow: '0 12px 26px rgba(0,0,0,0.22)'
                        }}
                        onClick={() => navigate(`/rosters/${roster.id}`)}
                        tabIndex={0}
                        role="button"
                      >
                        <div className="card-header d-flex justify-content-between align-items-center py-2 px-3" style={{ background: 'transparent', borderBottom: 'none' }}>
                          <div className="d-flex align-items-center gap-2">
                            <i className={`bi ${icon}`} style={{ fontSize: '1.5rem', color: iconColor, marginRight: '0.5rem' }}></i>
                            <div>
                              <h6 className="card-title mb-0 text-truncate text-light" style={{ fontSize: '1.1rem', fontWeight: 600 }}>{roster.displayName}</h6>
                              <small className="text-muted-brand fs-6">{roster.factions.length} fazioni</small>
                            </div>
                          </div>
                          <span className="badge" style={{ backgroundColor: borderColor, color: 'white', fontSize: '0.85em' }}>
                            {roster.gameType}
                          </span>
                        </div>
                        <div className="card-body py-2 px-3">
                          <div className="d-flex flex-column align-items-start">
                            <span className="text-muted-brand small">
                              Creato il {formatDate(roster.createdAt)}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
            </div>
          </>
        )}

        {/* Users Tab - List View */}
        {activeTab === 'users' && (
          <>
            <div className="mb-3">
              <input
                type="text"
                className="form-control stats-control"
                placeholder="🔍Cerca giocatore..."
                value={userSearch}
                onChange={e => setUserSearch(e.target.value)}
              />
            </div>
            <div className="list-group factions-users-list">
              {userList.map(user => (
                <div
                  key={user.uid}
                  className="list-group-item list-group-item-action factions-user-item clickable"
                  onClick={() => navigate(`/users/${user.uid}`)}
                  style={{ cursor: 'pointer' }}
                >
                  <div className="d-flex align-items-center">
                    {/* Avatar */}
                    <div className="position-relative me-3">
                      {user.profileImageUrl ? (
                        <img
                          src={user.profileImageUrl}
                          alt={user.username}
                          className="rounded-circle"
                          style={{ width: '50px', height: '50px', objectFit: 'cover' }}
                          onError={(e) => {
                            e.target.onerror = null;
                            e.target.style.display = 'none';
                            e.target.nextElementSibling.style.display = 'flex';
                          }}
                        />
                      ) : null}
                      <div
                        className={`rounded-circle d-flex align-items-center justify-content-center bg-secondary ${!user.profileImageUrl ? '' : 'd-none'}`}
                        style={{ width: '50px', height: '50px', position: user.profileImageUrl ? 'absolute' : 'relative', top: 0, left: 0 }}
                      >
                        <i className="bi bi-person-fill text-white"></i>
                      </div>
                    </div>

                    {/* User Info */}
                    <div className="flex-grow-1">
                      <div className="d-flex justify-content-between align-items-center">
                        <h5 className="mb-0 text-light">{user.username}</h5>
                        {/*user.faction && (
                <span className="text-success fw-bold">
                  {user.faction.score}

                </span>
              )*/}
                      </div>
                      {user.faction ? (
                        <small className="text-muted-brand">{user.faction.name}</small>
                      ) : (
                        <small className="text-muted-brand">Nessuna fazione</small>
                      )}
                    </div>

                    {/* Chevron icon for clickable items */}
                    <i className="bi bi-chevron-right text-muted-brand ms-2"></i>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}


        {/* Popup Legenda per Mobile */}
        {showLegend && (
          <div className="d-lg-none">
            {/* Overlay di sfondo */}
            <div
              className="modal-backdrop fade show"
              onClick={() => setShowLegend(false)}
            ></div>

            {/* Contenuto del popup */}
            <div
              className="modal fade show d-block"
              tabIndex="-1"
                style={{ position: 'fixed', top: 0, left: 0, right: 0, zIndex: 1050 }}
            >
              <div className="modal-dialog modal-dialog-centered">
                <div className="modal-content">
                  <div className="modal-header factions-side-card-header">
                    <h5 className="modal-title">Wrestler più scelti</h5>
                    <button
                      type="button"
                      className="btn-close btn-close-white"
                      onClick={() => setShowLegend(false)}
                    ></button>
                  </div>
                  <div className="modal-body p-0">
                    <ul className="list-group list-group-flush">
                      {topWrestlers.length > 0 ? (
                        topWrestlers.map((wrestler, index) => (
                          <li
                            key={wrestler.id}
                            className="list-group-item d-flex justify-content-between align-items-center factions-list-item"
                          >
                            <div className="d-flex align-items-center">
                              <span className="text-brand-secondary fw-bold me-2">{index + 1}.</span>
                              <span className="text-light">{wrestler.name}</span>
                            </div>
                            <span className="badge bg-brand-primary rounded-pill">
                              {wrestler.count}
                            </span>
                          </li>
                        ))
                      ) : (
                        <li className="list-group-item text-center text-muted-brand py-4 factions-list-item">
                          Nessun wrestler trovato
                        </li>
                      )}
                    </ul>
                  </div>
                  <div className="modal-footer small text-muted-brand factions-side-card-footer">
                    Numero di fazioni in cui compare
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default Factions;
